"use client";

import React from "react";
import styled, { css } from "styled-components";
import { Flex, Button, utils, renderMarkdown } from "@thenounproject/lingo-core";

const { getColor, getFont } = utils;

type Link = { text: string; url?: string; onClick?: () => unknown };
type Message = string | (string | Link)[];

type BannerBarProps = {
  message: string | Message;
  bannerStyle: "warning" | "alert";
  button?: { text: string; onClick?: () => void };
  onDismiss?: () => void;
  size?: "small" | "regular";
};

const Container = styled(Flex).attrs<Pick<BannerBarProps, "size" | "bannerStyle">>(props => ({
  as: "section",
  px: "m",
  height: props.size === "small" ? "40px" : "60px",
  alignItems: "center",
  background: props.bannerStyle === "warning" ? "info" : "error",
  flex: "0 0 auto",
}))<Pick<BannerBarProps, "size" | "bannerStyle">>``;

const MarkdownWrapper = styled.div<{ size: BannerBarProps["size"] }>`
  flex: 1 1 auto;
  text-align: center;
  color: white;
  ${props => css`
    ${getFont(props.size === "small" ? "ui.small" : "ui.regular", null)}
  `}
  p {
    margin: 0;
  }
  a {
    color: ${getColor("white")};
    text-decoration: underline;
  }
`;

const BannerBar: React.FC<BannerBarProps> = ({ message, bannerStyle, button, onDismiss, size }) => {
  function renderMessage() {
    if (typeof message === "string") {
      return (
        <MarkdownWrapper
          size={size}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(message) }}
        />
      );
    } else if (Array.isArray(message)) {
      return (
        <MarkdownWrapper size={size}>
          {message.map((part, index) => {
            if (typeof part === "string") {
              return <span key={index}>{part}</span>;
            } else {
              return part.url ? (
                <a key={index} href={part.url} target="_blank" rel="noopener noreferrer">
                  {part.text}
                </a>
              ) : (
                <button
                  key={index}
                  onClick={part.onClick}
                  style={{
                    color: "inherit",
                    textDecoration: "underline",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}>
                  {part.text}
                </button>
              );
            }
          })}
        </MarkdownWrapper>
      );
    }
  }
  return (
    <Container bannerStyle={bannerStyle} size={size}>
      {renderMessage()}
      <Flex gap="m" mr="s">
        {button && (
          <Button
            text={button.text}
            tintColor="white"
            buttonStyle="primary"
            themeOverrides={{
              primaryColor: "rgba(0,0,0,0.08)",
              primaryColorDark: "rgba(0,0,0,0.1)",
            }}
            size="small"
            onClick={button.onClick}
          />
        )}
        {onDismiss && (
          <Button
            icon="close"
            tintColor="white"
            buttonStyle="tertiary"
            size="small"
            aria-label="Dismiss banner"
            onClick={onDismiss}
          />
        )}
      </Flex>
    </Container>
  );
};

export default BannerBar;
